/**
 * PlaygroundPage Component
 *
 * Main playground interface with:
 * - Left sidebar: Session history with search
 * - Center: Browser streaming iframe
 * - Right: Settings panel, message input, and event display
 *
 * Refactored to be top-level (no project_id), using session_id and user_message fields.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePlaygroundSessions, useCreatePlaygroundSession, usePlaygroundSession } from '../hooks/usePlaygroundRuns';
import { usePlaygroundSSE } from '../hooks/usePlaygroundSSE';
import { PlaygroundSidebar } from '../components/playground/PlaygroundSidebar';
import { PlaygroundSettings } from '../components/playground/PlaygroundSettings';
import { EventStream } from '../components/playground/EventStream';
import { TopNav } from '../components/ui/TopNav';
import { JsonViewer } from '../components/ui/JsonViewer';
import { StepDataViewer } from '../components/playground/StepDataViewer';
import { HtmlTreeViewer } from '../components/playground/HtmlTreeViewer';
import { playgroundService } from '../services/playgroundApi';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { useStepData } from '../hooks/useStepData';
import toast from 'react-hot-toast';

export const PlaygroundPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get session ID from URL param (e.g., /playground?session=xxx)
  const urlSessionId = searchParams.get('session');

  // Local state for settings (UI-only, not persisted)
  const [browserType, setBrowserType] = useState<string>('stealth');
  const [useProxy, setUseProxy] = useState<boolean>(false);
  const [model, setModel] = useState<string>('gemini-3-flash-preview');
  const [gateway, setGateway] = useState<string>('default');
  const [message, setMessage] = useState<string>(() => {
    // Load draft from localStorage on mount
    return localStorage.getItem('playground_draft_message') || '';
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(urlSessionId);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);
  const [isNewSession, setIsNewSession] = useState<boolean>(false);
  const [shouldFocusSearch, setShouldFocusSearch] = useState<boolean>(false);
  const [showStopConfirm, setShowStopConfirm] = useState<boolean>(false);
  const [isStopping, setIsStopping] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);
  const [stepMode, setStepMode] = useState<boolean>(false);

  const stepData = useStepData();

  const [sessionLearnings, setSessionLearnings] = useState<string[]>([]);
  const [learningsTabRequested, setLearningsTabRequested] = useState(false);
  const [inspectionElementId, setInspectionElementId] = useState<number | null>(null);
  const [stepHtml, setStepHtml] = useState<string | null>(null);
  const [stepHtmlLoading, setStepHtmlLoading] = useState(false);
  const [liveHtmlLoading, setLiveHtmlLoading] = useState(false);

  // Draggable split ratio for step inspection panel (0-1, left portion)
  const [splitRatio, setSplitRatio] = useState(0.5);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const handleDividerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    const container = splitContainerRef.current;
    if (!container) return;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDraggingRef.current || !container) return;
      const rect = container.getBoundingClientRect();
      const ratio = (ev.clientX - rect.left) / rect.width;
      setSplitRatio(Math.max(0.2, Math.min(0.8, ratio)));
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  // Bidirectional URL sync: when activeSessionId changes, update URL
  useEffect(() => {
    if (activeSessionId) {
      setSearchParams({ session: activeSessionId }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [activeSessionId, setSearchParams]);

  // Save draft message to localStorage whenever it changes (for new session mode only)
  useEffect(() => {
    if (isNewSession && message) {
      localStorage.setItem('playground_draft_message', message);
    }
  }, [message, isNewSession]);

  // Auto-collapse sidebar on mobile + track mobile state
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) { // md breakpoint
        setIsSidebarOpen(false);
      }
    };
    handleResize(); // Check on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch session history (top-level, no project_id)
  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    refetch: refetchSessions,
  } = usePlaygroundSessions({
    limit: 100,
    offset: 0,
    search: searchQuery || undefined,
  });

  // Fetch active session details
  const { data: activeSession, refetch: refetchActiveSession } = usePlaygroundSession(
    activeSessionId || undefined
  );

  // Poll for status changes when running (every 2 seconds)
  useEffect(() => {
    if (activeSession?.status === 'running') {
      const interval = setInterval(async () => {
        const result = await refetchActiveSession();
        // If status changed from running to completed/failed, refresh the sessions list
        if (result.data && result.data.status !== 'running') {
          refetchSessions();
        }
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [activeSession?.status, refetchActiveSession, refetchSessions]);

  // SSE connection for active session - only connect if session is running
  const shouldConnect = !!activeSessionId && activeSession?.status === 'running';
  const {
    events: sseEvents,
    status,
    error,
    isRunning,
    streamingUrl: sseStreamingUrl,
    agentSpecId: sseAgentSpecId,
    waitingForConfirmation,
    pendingStepInfo,
    isStepMode: sseIsStepMode,
  } = usePlaygroundSSE(
    activeSessionId || undefined,
    shouldConnect
  );

  // Combine SSE events (for running sessions) with session_log (for completed sessions)
  const events = React.useMemo(() => {
    // For new session mode, show no events
    if (isNewSession) {
      return [];
    }

    if (activeSession?.status === 'running') {
      // For running sessions, use SSE events
      return sseEvents;
    } else if (activeSession?.session_log) {
      // For completed/failed sessions, use session_log from database
      return activeSession.session_log;
    }
    return [];
  }, [isNewSession, activeSession?.status, activeSession?.session_log, sseEvents]);

  // Create session mutation
  const createSessionMutation = useCreatePlaygroundSession();

  // Update active session when sessions data changes (select most recent session if none selected)
  useEffect(() => {
    if (!activeSessionId && sessionsData && sessionsData.sessions.length > 0) {
      setActiveSessionId(sessionsData.sessions[0].session_id);
    }
  }, [sessionsData, activeSessionId]);

  // Load previous message and settings when viewing a session (not in new session mode)
  useEffect(() => {
    if (!isNewSession && activeSession) {
      if (activeSession.user_message) {
        setMessage(activeSession.user_message);
      }
      // Always use TinyFish (stealth) — ignore stored browser_type
      setBrowserType('stealth');
      if (activeSession.use_proxy !== undefined) {
        setUseProxy(activeSession.use_proxy);
      }
    }
  }, [isNewSession, activeSession?.session_id]);

  const handleCreateSession = async (overrideStepMode?: boolean) => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    const useStepMode = overrideStepMode !== undefined ? overrideStepMode : stepMode;

    try {
      let session;

      if (activeSessionId && !isNewSession) {
        // Restart existing session - reuses same entry in history, creates new ADK session
        // This works for both finished and running sessions (though running shouldn't be clickable)
        const { playgroundService } = await import('../services/playgroundApi');
        session = await playgroundService.restartSession(activeSessionId, {
          user_message: message.trim(),
          browser_type: browserType,
          use_proxy: useProxy,
          model: model,
          step_mode: useStepMode,
          step_replay_mode: 'record',
          gateway: gateway !== 'default' ? gateway : undefined,
        });
        toast.success('Session restarted!');

        // Invalidate and refetch the specific session to get cleared state
        queryClient.invalidateQueries({ queryKey: ['playground-session', activeSessionId] });
        await refetchActiveSession();
      } else {
        // Create new session - adds new entry to history
        session = await createSessionMutation.mutateAsync({
          user_message: message.trim(),
          browser_type: browserType,
          use_proxy: useProxy,
          model: model,
          step_mode: useStepMode,
          step_replay_mode: 'record',
          gateway: gateway !== 'default' ? gateway : undefined,
        });
        toast.success('Playground session started!');
        // Clear draft from localStorage when starting new session
        localStorage.removeItem('playground_draft_message');
        // Don't clear message - keep it visible while running
      }

      // Clear new session flag and switch to the created/restarted session
      setIsNewSession(false);
      setActiveSessionId(session.session_id);

      // Auto-hide sidebar to show more space for browser streaming
      setIsSidebarOpen(false);

      // Refresh session history and active session data
      refetchSessions();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start playground session');
    }
  };

  const handleSessionSelect = (sessionId: string) => {
    setActiveSessionId(sessionId);
    setIsNewSession(false); // Clear new session flag when selecting existing session
    stepData.closeStepData();
  };

  // Handle clicking an individual step event
  const handleStepSelect = async (functionCallId: string) => {
    setLearningsTabRequested(false);
    const logSessionId = activeSession?.web_agent_session_id || activeSessionId;
    if (!logSessionId) return;
    await stepData.selectStep(logSessionId, functionCallId);
  };

  const handleLearningsClick = async (learnings: string[]) => {
    setSessionLearnings(learnings);
    setLearningsTabRequested(true);
    // Open the data panel on step 1 (just to have a step context), then switch to learnings tab
    const logSessionId = activeSession?.web_agent_session_id || activeSessionId;
    if (!logSessionId) return;
    // Find the first action's functionCallId from events
    const firstAction = events.find(e => e.type === 'action');
    if (firstAction?.function_call_id) {
      await stepData.selectStep(logSessionId, firstAction.function_call_id);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
  };

  const handleNewSession = () => {
    // If already in new session mode, do nothing
    if (isNewSession) {
      return;
    }
    // Activate new session UI (no backend object created)
    setIsNewSession(true);
    setActiveSessionId(null);
    // Restore draft message from localStorage
    const draft = localStorage.getItem('playground_draft_message') || '';
    setMessage(draft);
    setBrowserType('stealth');
    setUseProxy(false);
    setModel('gemini-3-flash-preview');
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleRenameSession = async (sessionId: string, newName: string) => {
    try {
      const { playgroundService } = await import('../services/playgroundApi');
      await playgroundService.renameSession(sessionId, newName);
      toast.success('Session renamed!');
      refetchSessions();
      if (sessionId === activeSessionId) {
        refetchActiveSession();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to rename session');
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      const { playgroundService } = await import('../services/playgroundApi');
      await playgroundService.deleteSession(sessionId);
      toast.success('Session deleted!');

      // If deleting active session, clear selection
      if (sessionId === activeSessionId) {
        setActiveSessionId(null);
        setIsNewSession(true); // Show new session UI
      }

      refetchSessions();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete session');
    }
  };

  /** Format pending step info into a human-readable label like "click 4" or "input_text 12 'hello'" */
  const formatPendingStep = (info: typeof pendingStepInfo): string => {
    if (!info) return '';
    const name = info.tool_name
      .replace(/_tool$/, '')
      .replace(/_element$/, '')
      .replace(/_/g, ' ');
    const parts = [name];
    if (info.args?.element_id != null) parts.push(String(info.args.element_id));
    if (info.args?.text) {
      const t = info.args.text;
      parts.push(`"${t.length > 20 ? t.slice(0, 20) + '...' : t}"`);
    }
    if (info.args?.url) {
      const u = info.args.url;
      parts.push(u.length > 30 ? u.slice(0, 30) + '...' : u);
    }
    if (info.args?.key) parts.push(info.args.key);
    return parts.join(' ');
  };

  const [confirmPending, setConfirmPending] = useState(false);

  // Reset confirmPending when a new confirmation_required event arrives.
  // We track pendingStepInfo.step_number — it changes each time a new step pauses,
  // even if waitingForConfirmation stays true (React batching can skip false→true).
  useEffect(() => {
    if (pendingStepInfo?.step_number != null) setConfirmPending(false);
  }, [pendingStepInfo?.step_number]);

  const handleConfirmStep = async () => {
    if (!activeSessionId || confirmPending) return;
    setConfirmPending(true);
    try {
      const { playgroundService } = await import('../services/playgroundApi');
      await playgroundService.confirmStep(activeSessionId);
    } catch (error) {
      setConfirmPending(false);
      toast.error(error instanceof Error ? error.message : 'Failed to confirm step');
    }
  };

  const handleStopSession = async () => {
    if (!activeSessionId) return;

    // Close dialog immediately
    setShowStopConfirm(false);

    try {
      setIsStopping(true);
      const { playgroundService } = await import('../services/playgroundApi');
      await playgroundService.stopSession(activeSessionId);
      toast.success('Session stopped');

      // Refresh session data immediately
      await refetchActiveSession();
      await refetchSessions();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to stop session');
      setIsStopping(false);
    }
  };

  // Reset isStopping when session finishes — via session status OR SSE stream ending
  useEffect(() => {
    if (isStopping && (
      (activeSession?.status && !['running', 'cancelling'].includes(activeSession.status)) ||
      !isRunning
    )) {
      setIsStopping(false);
    }
  }, [isStopping, activeSession?.status, isRunning]);

  // Check if session is actually running (prefer session status over SSE isRunning flag)
  // Also treat waitingForConfirmation as running — SSE event can arrive before session query refreshes
  // "cancelling" means stop was requested but EVA hasn't finished cleanup yet — treat as still active.
  const isCancelling = activeSession?.status === 'cancelling';
  const isSessionRunning = activeSession?.status === 'running' || isCancelling || waitingForConfirmation;
  const isInputDisabled = isSessionRunning || createSessionMutation.isPending;
  const isFinishedSession = !isNewSession && activeSession && ['completed', 'failed'].includes(activeSession.status);
  const shouldShowInput = !isSessionRunning && !createSessionMutation.isPending;
  const isCreatingSession = createSessionMutation.isPending;

  // Get final output from SSE events or session.output
  const finalOutput = React.useMemo(() => {
    // Don't show final output for new session mode
    if (isNewSession) {
      return null;
    }

    // Check events for final_response first (for both running and completed sessions)
    const finalResponseEvent = events.find(event => event.type === 'final_response');
    if (finalResponseEvent?.data) {
      // Handle double-nested structure: event.data.data contains actual result
      const actualData = finalResponseEvent.data.data || finalResponseEvent.data;
      return actualData;
    }
    // Fall back to session.output (from database)
    return activeSession?.output;
  }, [isNewSession, events, activeSession?.output]);

  // Get streaming URL from SSE (for running sessions) or session data (for completed sessions)
  // Don't show streaming URL or final output for new session mode
  // Don't show streaming URL for completed sessions with no output (canceled)
  const streamingUrl = React.useMemo(() => {
    if (isNewSession) return null;
    // Hide stream when session is finished — browser session is already cleaned up
    if (activeSession?.status === 'completed' || activeSession?.status === 'failed') return null;
    return sseStreamingUrl || activeSession?.streaming_url;
  }, [isNewSession, activeSession?.status, activeSession?.streaming_url, sseStreamingUrl]);

  const handleCollapsedNewSession = () => {
    setIsSidebarOpen(true);
    handleNewSession();
  };

  const handleCollapsedSearch = () => {
    setIsSidebarOpen(true);
    setShouldFocusSearch(true);
    // Reset focus flag after a delay
    setTimeout(() => setShouldFocusSearch(false), 200);
  };

  // Fetch HTML snapshot when a step is selected
  useEffect(() => {
    if (stepData.isViewingStepData && stepData.selectedSessionId && stepData.selectedStep !== null) {
      setStepHtml(null);
      setStepHtmlLoading(true);
      setInspectionElementId(null);
      playgroundService.getStepHTML(stepData.selectedSessionId, stepData.selectedStep)
        .then((html) => setStepHtml(html || null))
        .catch(() => setStepHtml(null))
        .finally(() => setStepHtmlLoading(false));
    } else {
      setStepHtml(null);
      setInspectionElementId(null);
    }
  }, [stepData.isViewingStepData, stepData.selectedSessionId, stepData.selectedStep]);

  const handleFetchLiveHtml = async () => {
    if (!stepData.selectedSessionId) return;
    setLiveHtmlLoading(true);
    try {
      const html = await playgroundService.getPageHTML(stepData.selectedSessionId);
      setStepHtml(html || null);
    } catch {
      // ignore
    } finally {
      setLiveHtmlLoading(false);
    }
  };

  const handleHtmlElementClick = async (elementId: number) => {
    setInspectionElementId(elementId);
    if ((isRunning || waitingForConfirmation) && activeSessionId) {
      try {
        await playgroundService.highlightElement(activeSessionId, elementId);
      } catch {}
    }
  };

  const handleABoxElementSelect = async (elementId: number | null) => {
    setInspectionElementId(elementId);
    if ((isRunning || waitingForConfirmation) && activeSessionId) {
      try {
        if (elementId !== null) {
          await playgroundService.highlightElement(activeSessionId, elementId);
        } else {
          await playgroundService.clearHighlight(activeSessionId);
        }
      } catch {}
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col overflow-hidden">
    <TopNav playgroundControls={{
      onNewSession: handleNewSession,
      onToggleSidebar: toggleSidebar,
      onSearchFocus: handleCollapsedSearch,
      isSidebarOpen,
      isNewSession,
    }} />
    <div className="flex-1 flex overflow-hidden bg-gray-50">
      {/* Left Sidebar - Session History */}
      {isSidebarOpen && (
        <PlaygroundSidebar
          sessions={sessionsData?.sessions || []}
          activeSessionId={activeSessionId}
          isNewSession={isNewSession}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          onSessionSelect={handleSessionSelect}
          onNewSession={handleNewSession}
          onToggleSidebar={toggleSidebar}
          onSearchFocus={shouldFocusSearch ? () => {} : undefined}
          onRenameSession={handleRenameSession}
          onDeleteSession={handleDeleteSession}
          isLoading={sessionsLoading}
        />
      )}

      {/* Main Content - Responsive Layout */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Mobile: Vertical Stack | Desktop: Horizontal Split */}
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {/* Center - Browser Streaming, Final Output, or Log Viewer */}
          {/* Always render the same DOM structure so the iframe never unmounts when the step panel opens/closes */}
          <div ref={splitContainerRef} className="w-full h-[40vh] shrink-0 md:shrink md:h-full md:flex-1 bg-white overflow-hidden flex">
            {/* Left half: stream + HTML viewer (full-width when panel closed) */}
            <div className="h-full flex flex-col overflow-hidden"
                 style={stepData.isViewingStepData && stepData.selectedSessionId
                   ? { width: `${splitRatio * 100}%`, minWidth: 200 }
                   : { width: '100%' }}>
              {/* Main content area */}
              <div className={`${stepData.isViewingStepData ? 'flex-[6]' : 'flex-1'} min-h-0`}>
                {isStopping || (activeSession?.status === 'completed' && !finalOutput && !streamingUrl) ? (
                  <div className="flex items-start justify-center h-full bg-white pt-[20%]">
                    <div className="text-center text-gray-400">
                      <div className="text-6xl mb-4">☁️</div>
                      <p className="text-sm">Session canceled by user</p>
                    </div>
                  </div>
                ) : isCreatingSession || (isSessionRunning && !streamingUrl && !isStopping) ? (
                  <div className="flex items-start justify-center h-full bg-white pt-[20%]">
                    <div className="text-center text-gray-400">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-sm">Initializing browser...</p>
                    </div>
                  </div>
                ) : finalOutput && !isSessionRunning ? (
                  <div className="h-full overflow-auto bg-white p-6">
                    {typeof finalOutput === 'string' ? (
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap break-all font-mono leading-relaxed">
                        {finalOutput}
                      </pre>
                    ) : (
                      <JsonViewer data={finalOutput} />
                    )}
                  </div>
                ) : streamingUrl ? (
                  <iframe
                    src={streamingUrl}
                    className="w-full h-full border-0 pointer-events-none"
                    title="Browser streaming"
                    sandbox="allow-same-origin allow-scripts"
                  />
                ) : (
                  <div className="flex items-start justify-center h-full bg-white pt-[20%]">
                    <div className="text-center text-gray-400">
                      <svg
                        className="mx-auto h-12 w-12 mb-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-sm">Waiting for session to start</p>
                    </div>
                  </div>
                )}
              </div>
              {/* HTML Tree Viewer — only visible when step panel is open */}
              {stepData.isViewingStepData && (
                <div className="flex-[4] min-h-0 border-t border-gray-200">
                  {stepHtmlLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : stepHtml ? (
                    <HtmlTreeViewer
                      html={stepHtml}
                      highlightElementId={inspectionElementId}
                      onElementClick={handleHtmlElementClick}
                      label={`Step ${stepData.selectedStep} HTML`}
                      onFetchLive={(isRunning || waitingForConfirmation) ? handleFetchLiveHtml : undefined}
                      isLiveLoading={liveHtmlLoading}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                      No HTML snapshot for this step
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Drag divider */}
            {stepData.isViewingStepData && stepData.selectedSessionId && (
              <div
                className="h-full w-1 flex-shrink-0 cursor-col-resize bg-gray-200 hover:bg-blue-400 active:bg-blue-500 transition-colors"
                onMouseDown={handleDividerMouseDown}
              />
            )}
            {/* Right half: Step data panel — only visible when open */}
            {stepData.isViewingStepData && stepData.selectedSessionId && (
              <div className="h-full border-l border-gray-200 overflow-hidden" style={{ width: `${(1 - splitRatio) * 100}%`, minWidth: 200 }}>
                {stepData.isLoadingStep ? (
                  <div className="flex items-center justify-center h-full bg-white">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <span className="text-xs text-gray-400">Loading step data...</span>
                    </div>
                  </div>
                ) : stepData.selectedStep !== null ? (
                  <StepDataViewer
                    sessionId={stepData.selectedSessionId}
                    stepNumber={stepData.selectedStep}
                    isLive={isRunning || waitingForConfirmation}
                    onClose={() => { stepData.closeStepData(); setLearningsTabRequested(false); }}
                    onElementSelect={handleABoxElementSelect}
                    highlightedId={inspectionElementId}
                    learnings={sessionLearnings}
                    initialTab={learningsTabRequested ? 'learnings' : undefined}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-white">
                    <span className="text-xs text-gray-400">No step data found</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - Settings, Input, and Events */}
          <div className="w-full flex-1 min-h-0 md:flex-none md:h-full md:w-96 bg-white flex flex-col overflow-hidden">
          {/* Settings and Input - Docked at Top */}
          <div className="flex-shrink-0 px-4 py-3">
            <PlaygroundSettings
              browserType={browserType}
              useProxy={useProxy}
              model={model}
              onBrowserTypeChange={setBrowserType}
              onUseProxyChange={setUseProxy}
              onModelChange={setModel}
              gateway={gateway}
              onGatewayChange={setGateway}
              disabled={isSessionRunning || createSessionMutation.isPending}
            />

            {/* Message Input */}
            <div className="space-y-2">
              <div className="relative group">
                <textarea
                  id="message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g., Navigate to google.com and search for 'React hooks'"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
                  rows={isMobile ? 3 : 6}
                  disabled={isInputDisabled}
                  aria-label="Message input"
                />
                {/* Copy Button - Show on hover */}
                {message && !isInputDisabled && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(message);
                      toast.success('Copied to clipboard!');
                    }}
                    className="absolute top-2 right-2 p-1.5 bg-white border border-gray-300 rounded hover:bg-gray-50 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                    aria-label="Copy instruction"
                    title="Copy instruction"
                  >
                    <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                {/* Next button (step mode: waiting for confirmation) */}
                {isSessionRunning && waitingForConfirmation ? (
                  <button
                    onClick={handleConfirmStep}
                    disabled={confirmPending}
                    className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {confirmPending ? 'Running...' : 'Next'}
                  </button>
                ) : isSessionRunning ? (
                  /* Stop button (session running, not waiting) */
                  <button
                    onClick={() => setShowStopConfirm(true)}
                    disabled={isStopping || isCancelling}
                    className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-white text-red-600 border-[0.5px] border-red-600 hover:bg-red-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isStopping || isCancelling ? 'Stopping...' : 'Stop'}
                  </button>
                ) : (
                  /* Run + Step buttons (session not running) */
                  <>
                    <button
                      onClick={() => { setStepMode(false); handleCreateSession(false); }}
                      disabled={isInputDisabled || !message.trim()}
                      className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createSessionMutation.isPending && !stepMode ? 'Starting...' : 'Run'}
                    </button>
                    <button
                      onClick={() => { setStepMode(true); handleCreateSession(true); }}
                      disabled={isInputDisabled || !message.trim()}
                      className="px-4 py-2 text-sm font-medium rounded-md bg-white text-blue-600 border border-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Run step-by-step (pause before each tool call)"
                    >
                      {createSessionMutation.isPending && stepMode ? 'Starting...' : 'Step'}
                    </button>
                  </>
                )}
                {/* Stop button always visible in step mode while running */}
                {isSessionRunning && waitingForConfirmation && (
                  <button
                    onClick={() => setShowStopConfirm(true)}
                    disabled={isStopping || isCancelling}
                    className="px-4 py-2 text-sm font-medium rounded-md bg-white text-red-600 border-[0.5px] border-red-600 hover:bg-red-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Stop session"
                  >
                    {isStopping || isCancelling ? '...' : 'Stop'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Stop Confirmation Dialog */}
          <ConfirmDialog
            isOpen={showStopConfirm}
            title="Stop Session"
            message="Are you sure you want to stop this running session? This action cannot be undone."
            confirmLabel="Stop"
            cancelLabel="Cancel"
            onConfirm={handleStopSession}
            onCancel={() => setShowStopConfirm(false)}
            isDestructive={true}
          />

            {/* Events Display - Flat Stream */}
            <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
              <EventStream
                events={events}
                startTime={activeSession?.started_at}
                endTime={activeSession?.completed_at}
                autoScroll={isSessionRunning}
                alwaysExpanded={true}
                agentSpecId={sseAgentSpecId || activeSession?.agent_spec_id}
                sessionId={activeSession?.web_agent_session_id || activeSessionId || undefined}
                onStepSelect={handleStepSelect}
                selectedFunctionCallId={stepData.selectedFunctionCallId}
                waitingForConfirmation={waitingForConfirmation}
                pendingToolName={formatPendingStep(pendingStepInfo)}
                isStepMode={stepMode || sseIsStepMode}
                onLearningsClick={handleLearningsClick}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};
